import { DatumVerticalUsageType, datumUsageNew } from "building-types/datums/datumUsageTypes";
import type { Doc } from "building-types/doc";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type {
  BlockHandle,
  ComponentAssociatedEntityHandle,
  ComponentInstanceHandle,
  ComponentMemberEntityHandle,
  ComponentMemberEntityType,
  FloorSystemHandle,
  FoundationSystemHandle,
  LevelHandle,
  OptionSetHandle,
  RoofSystemHandle,
} from "building-types/handles/docEntityHandleTypes";
import {
  type ComponentHandle,
  type DocEntityHandle,
  type IsolatableEntityHandle,
  type LevelHostedEntityHandle,
  isComponentAssociatedEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { LevelDef } from "building-types/levels/LevelDef";
import {
  datumLevelPlacementNew,
  isDatumLevelPlacement,
  isStackedLevelPlacement,
  stackedLevelPlacementNew,
} from "building-types/levels/levelTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { mergeIn, setIn } from "common/utils/objectUtils";
import { nextSortIndexFromTable } from "common/utils/sortIndex";
import uid from "common/utils/uid";
import { docAddDefaultViewsForLevels, docEnsureComponentDefaultViews } from "./docAddDefaultViewsForLevels";
import { docGetFoundationSystemFoundations } from "./docGetFoundationSystems";
import { docSyncRefMembership } from "./docRefMembershipTools";
import { docRemapFloorRoomRefsForComponent } from "./docRemapFloorRoomRefsForComponent";
import { docRemapStairBlockRefsForComponent, levelIdsFromStairBlockDatums } from "./docRemapStairBlockRefsForComponent";
import { blockNew } from "./elements/blockNew";
import { docEntityHandleHash } from "./entity/core/handleHash";
import { parseEntityInfoFromHandleHash } from "./entity/docEntityHandleHash";
import { DocEntityHandleSet } from "./entity/DocEntityHandleSet";
import { docEntityHandleIsLabelable } from "./entity/docEntityHandleTools";
import { docEntityTableUpdateSome } from "./entity/docEntityTableUpdateChildren";
import {
  ComponentCircularDependencyErrorNotification,
  DocEntityTransactErrorNotification,
} from "./entity/docEntityTransactErrorNotification";
import {
  docAnnotationIsLevelHosted,
  docEntityBelongsToComponent,
  docEntityGetComponent,
  isComponentMemberEntityHandle,
} from "./entity/docGetEntityComponent";
import { docGetEntityId } from "./entity/docGetEntityId";
import { docGetEntityGroup } from "./entity/docGetGroup";
import { docGetComponentEntities } from "./entity/docGetHashTableEntities";
import { docGetFloorSystemFloorHandles } from "./entity/docGetSystemChildren";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { docGetComponentIdsForAssociatedEntity } from "./options/componentAssociations";
import { isVirtualRoofBlock, roofBlockIds } from "./roofSystemTools";
import { docEntityHasAnyReportableUnitDesignatorLabel } from "./utils/labelTools";

/**
 * Returns true if the doc contains any nested component instances — i.e. a
 * COMPONENT_INSTANCE that is itself a member of a component. Top-level instances
 * (placed directly in a plan view) are not members, so they don't count.
 */
export function docHasNestedComponentInstances(doc: Pick<Doc, "componentMembers">): boolean {
  for (const hash in doc.componentMembers) {
    if (parseEntityInfoFromHandleHash(hash)?.entityType === "COMPONENT_INSTANCE") {
      return true;
    }
  }
  return false;
}

/**
 * Gets all entities of a specific type that belong to a component.
 */
export function docGetComponentMembersByType<T extends ComponentMemberEntityType>(
  doc: Doc,
  componentHandle: ComponentHandle,
  entityType: T,
): Extract<DocEntityHandle, { type: T }>[] {
  return docGetComponentEntities(doc, componentHandle).filter(
    (handle): handle is Extract<DocEntityHandle, { type: T }> => handle.type === entityType,
  );
}

function docAllEntityIdsForComponentMemberType(doc: Doc, entityType: ComponentMemberEntityType): string[] {
  switch (entityType) {
    case "BLOCK":
      return Object.keys(doc.blocks);
    case "LEVEL":
      return Object.keys(doc.levels);
    case "ROOM":
      return Object.keys(doc.rooms);
    case "ROOF":
      return Object.keys(doc.roofs);
    case "STAIR_SYSTEM":
      return Object.keys(doc.stairSystems);
    case "RAILING":
      return Object.keys(doc.railings);
    case "COMPONENT_INSTANCE":
      return Object.keys(doc.componentInstances);
    case "OPTION_SET":
      return Object.keys(doc.optionSets);
    case "ROOF_SYSTEM":
      return Object.keys(doc.roofSystems);
    case "FOUNDATION_SYSTEM":
      return Object.keys(doc.foundationSystems);
    case "FLOOR_SYSTEM":
      return Object.keys(doc.floorSystems);
    default: {
      assertUnreachable(entityType);
    }
  }
}

/**
 * Entity handles of a component-member type that belong to the effective isolation world:
 * when `isolatedEntity` is a component, members of that component; when `undefined` (main
 * document world), entities of that type that are not assigned to any component via
 * componentMembers or componentAssociations. Same doc-level membership rule used for studio
 * isolation and roof perimeter.
 */
export function docGetEntityHandlesForIsolationWorld<T extends ComponentMemberEntityType>(
  doc: Doc,
  isolatedEntity: IsolatableEntityHandle | undefined,
  entityType: T,
): Extract<DocEntityHandle, { type: T }>[] {
  if (isolatedEntity?.type === "COMPONENT") {
    return docGetComponentMembersByType(doc, isolatedEntity, entityType);
  }

  const ids = docAllEntityIdsForComponentMemberType(doc, entityType);
  const result: Extract<DocEntityHandle, { type: T }>[] = [];
  for (const id of ids) {
    const handle = { type: entityType, id } as Extract<DocEntityHandle, { type: T }>;
    if (!docEntityBelongsToComponent(doc, handle)) {
      result.push(handle);
    }
  }
  return result;
}

/**
 * For a given level, find the id of the level it should stack above in the original stack.
 */
function getNextLowerLevelOldId(levelDef: LevelDef, sortedOldLevelIds: string[]): string | undefined {
  const { placement } = levelDef;
  if (isStackedLevelPlacement(placement)) {
    if (!placement.automaticStacking && placement.stackAbove) {
      return placement.stackAbove;
    }
    if (placement.automaticStacking) {
      const index = sortedOldLevelIds.indexOf(levelDef.id);
      return index > 0 ? sortedOldLevelIds[index - 1] : undefined;
    }
  }
  return undefined;
}

/**
 * For a given level, find the id of the level it should stack above in the new stack.
 */
function getNextLowerLevelNewId(
  doc: Doc,
  oldId: string,
  oldToNewLevelId: Record<string, string>,
  sortedOldLevelIds: string[],
): string | undefined {
  let currentLevelId: string | undefined = oldId;

  while (currentLevelId) {
    const lowerLevelId = getNextLowerLevelOldId(doc.levels[currentLevelId], sortedOldLevelIds);
    if (!lowerLevelId) {
      return undefined;
    }

    if (oldToNewLevelId[lowerLevelId]) {
      return oldToNewLevelId[lowerLevelId];
    }
    currentLevelId = lowerLevelId;
  }
  return undefined;
}

export function updateLevelPlacement(doc: Doc, oldToNewLevelId: Record<string, string>): Doc {
  const newLevelIds = new Set(Object.values(oldToNewLevelId));
  const oldLevels = Object.values(doc.levels).filter((l) => !newLevelIds.has(l.id));
  const sortedOldLevelIds = oldLevels.sort((a, b) => a.sortIndex - b.sortIndex).map((l) => l.id);

  // After we duplicate the levels, we need to create an independent stack for the new levels
  // that mirrors the existing stack for the original levels they were copied from.
  for (const oldId in oldToNewLevelId) {
    const newId = oldToNewLevelId[oldId];
    const newLevel = doc.levels[newId];
    if (!newLevel) {
      continue;
    }

    let { placement } = newLevel;
    if (isStackedLevelPlacement(placement)) {
      const lowerLevelId = getNextLowerLevelNewId(doc, oldId, oldToNewLevelId, sortedOldLevelIds);
      if (lowerLevelId) {
        // Ancestor was also duplicated: keep stacking on the new copy of that ancestor.
        placement = { ...placement, stackAbove: lowerLevelId };
      } else {
        const oldLevelDef = doc.levels[oldId];
        const wasBottomOfOriginalStack =
          oldLevelDef !== undefined && getNextLowerLevelOldId(oldLevelDef, sortedOldLevelIds) === undefined;
        if (wasBottomOfOriginalStack) {
          // Bottom of the original stack: keep stacked with nothing below, like a default world level.
          placement = stackedLevelPlacementNew(true);
        } else {
          // Mid-stack level copied without its ancestors: anchor independently at grade.
          placement = datumLevelPlacementNew(datumUsageNew(DatumVerticalUsageType.LEVEL_PLACEMENT, ""));
        }
      }
    } else if (isDatumLevelPlacement(placement)) {
      const aboveLevelId = placement.aboveLevelId ? oldToNewLevelId[placement.aboveLevelId] : undefined;
      const belowLevelId = placement.belowLevelId ? oldToNewLevelId[placement.belowLevelId] : undefined;
      placement = { ...placement, aboveLevelId, belowLevelId };
    }

    if (placement !== newLevel.placement) {
      doc = mergeIn(doc, ["levels", newId], { placement });
    }
  }
  return doc;
}

export function expandRoofRelatedMemberHandles(
  doc: Doc,
  memberHandles: ComponentMemberEntityHandle[],
): ComponentMemberEntityHandle[] {
  const expandedHandles = [...memberHandles];
  const memberRoofIds = new Set<string>();
  const roofSystemIds = new Set<string>();
  let hasRoofSystemHandle = false;

  // Find all roof and roof system IDs.
  for (const handle of memberHandles) {
    if (handle.type === "ROOF") {
      memberRoofIds.add(handle.id);
    } else if (handle.type === "ROOF_SYSTEM") {
      roofSystemIds.add(handle.id);
      hasRoofSystemHandle = true;
    }
  }

  // Add the children roofs of the roof systems.
  const resultRoofIds = new Set<string>(memberRoofIds);
  if (hasRoofSystemHandle) {
    for (const roof of Object.values(doc.roofs)) {
      if (roof.parentId && roofSystemIds.has(roof.parentId)) {
        if (!resultRoofIds.has(roof.id)) {
          resultRoofIds.add(roof.id);
          expandedHandles.push({ type: "ROOF", id: roof.id });
        }
      }
    }
  }
  return expandedHandles;
}

/**
 * When adding a roof whose parent roof system is not in the component, reparent it to a new roof system
 * inside the component.
 */
function docReparentComponentRoofs(
  doc: Doc,
  inputHandles: ComponentMemberEntityHandle[],
): { doc: Doc; handles: ComponentMemberEntityHandle[] } {
  const handles = [...inputHandles];
  const handlesSet = new DocEntityHandleSet(handles);
  const oldToNewRoofSystemId: Record<string, string> = {};

  // For each roof whose parent roof system is not being added, create a new roof system for it.
  for (const handle of handles) {
    if (handle.type !== "ROOF") {
      continue;
    }
    const oldParentId = doc.roofs[handle.id]?.parentId;
    const parentHandle: RoofSystemHandle = { type: "ROOF_SYSTEM", id: oldParentId };
    if (handlesSet.has(parentHandle)) {
      // Skip if the parent is already being added.
      continue;
    }

    // Create a new parent roof system, if it doesn't exist.
    let newParentId = oldToNewRoofSystemId[oldParentId];
    if (!newParentId) {
      newParentId = uid();
      const oldRoofSystem = doc.roofSystems[oldParentId];
      const sortIndex = nextSortIndexFromTable(doc.roofSystems);
      const newRoofSystem = { ...oldRoofSystem, id: newParentId, sortIndex };
      doc = mergeIn(doc, ["roofSystems", newParentId], newRoofSystem);
      oldToNewRoofSystemId[oldParentId] = newParentId;
      handles.push({ type: "ROOF_SYSTEM", id: newParentId });
    }
    doc = mergeIn(doc, ["roofs", handle.id], { parentId: newParentId });
  }

  return { doc, handles };
}

/** Reparent a level-hosted entity to the component's target level before adding to the component. */
export function docAssignLevelHostedEntityToComponentLevel(
  doc: Doc,
  handle: LevelHostedEntityHandle,
  targetLevelId: string,
): Doc {
  switch (handle.type) {
    case "STAIR_SYSTEM":
      return mergeIn(doc, ["stairSystems", handle.id], { levelId: targetLevelId });
    case "RAILING":
      return mergeIn(doc, ["railings", handle.id], { levelId: targetLevelId });
    case "FREEFORM_WALL":
      return mergeIn(doc, ["freeformWalls", handle.id], { levelId: targetLevelId });
    case "ANNOTATION":
      return mergeIn(doc, ["annotation", handle.id], { parentId: targetLevelId });
    case "FOUNDATION_SYSTEM":
      for (const foundation of docGetFoundationSystemFoundations(doc, handle)) {
        if (foundation.levelId) {
          doc = mergeIn(doc, ["foundations", foundation.id], { levelId: targetLevelId });
        }
      }
      return doc;
    case "FLOOR_SYSTEM":
      for (const floorHandle of docGetFloorSystemFloorHandles(doc, handle)) {
        const floor = doc.floors[floorHandle.id];
        if (floor?.levelId) {
          doc = mergeIn(doc, ["floors", floorHandle.id], { levelId: targetLevelId });
        }
      }
      return doc;
    default:
      assertUnreachable(handle);
  }
}

/**
 * For the rooms, stair systems, railings, and level-hosted annotations added to a component, ensure
 * their levels and blocks are also added (as copies) to the component: and update each to point
 * to the new component's levels and blocks.
 * When oldToNewLevelIdOut is provided, a mapping of old level ids to new level ids is merged into it.
 * refHandles may include ANNOTATION handles for level-hosted annotations whose levels should be copied.
 */
export function docCopyStructureForMembers(
  doc: Doc,
  componentId: string,
  initialMemberHandles: ComponentMemberEntityHandle[],
  oldToNewLevelIdOut?: Record<string, string>,
  refHandles?: readonly DocEntityHandle[],
): Doc {
  const memberHandles = expandRoofRelatedMemberHandles(doc, initialMemberHandles);
  const roomHandles = memberHandles.filter((h): h is Extract<DocEntityHandle, { type: "ROOM" }> => h.type === "ROOM");
  const stairHandles = memberHandles.filter(
    (h): h is Extract<DocEntityHandle, { type: "STAIR_SYSTEM" }> => h.type === "STAIR_SYSTEM",
  );
  const railingHandles = memberHandles.filter(
    (h): h is Extract<DocEntityHandle, { type: "RAILING" }> => h.type === "RAILING",
  );
  const foundationSystemHandles = initialMemberHandles.filter(
    (h): h is FoundationSystemHandle => h.type === "FOUNDATION_SYSTEM",
  );
  const floorSystemHandles = initialMemberHandles.filter((h): h is FloorSystemHandle => h.type === "FLOOR_SYSTEM");
  const roofHandles = memberHandles.filter((h): h is Extract<DocEntityHandle, { type: "ROOF" }> => h.type === "ROOF");

  const oldBlockIds = new Set<string>();
  const oldLevelIds = new Set<string>();
  const oldRoofSystemIds = new Set<string>();
  const levelHostedAnnotationIds = new Set<string>();

  const roomIdToBlockId: Record<string, string> = {};
  for (const h of roomHandles) {
    const parentId = doc.rooms[h.id]?.parentId;
    if (parentId) {
      roomIdToBlockId[h.id] = parentId;
      oldBlockIds.add(parentId);
    }
  }
  const roofIdToBlockIds: Record<string, string[]> = {};
  for (const h of roofHandles) {
    const roof = doc.roofs[h.id];
    if (roof) {
      if (roof.parentId) {
        oldRoofSystemIds.add(roof.parentId);
      }

      if (isVirtualRoofBlock(roof.blocks)) {
        const levelId = roof.blocks.levelUsage.entityId;
        oldLevelIds.add(levelId);
      } else {
        const blockIds = roofBlockIds(roof.blocks);
        roofIdToBlockIds[h.id] = blockIds;
        blockIds.forEach((id) => oldBlockIds.add(id));
      }
    }
  }

  for (const blockId of oldBlockIds) {
    const levelId = doc.blocks[blockId]?.levelId;
    if (levelId) {
      oldLevelIds.add(levelId);
    }
  }
  const stairSystemIds = new Set(stairHandles.map((h) => h.id));
  for (const h of stairHandles) {
    const levelId = doc.stairSystems[h.id]?.levelId;
    if (levelId) {
      oldLevelIds.add(levelId);
    }
  }
  for (const levelId of levelIdsFromStairBlockDatums(doc, stairSystemIds)) {
    oldLevelIds.add(levelId);
  }
  for (const h of railingHandles) {
    const levelId = doc.railings[h.id]?.levelId;
    if (levelId) {
      oldLevelIds.add(levelId);
    }
  }
  for (const h of foundationSystemHandles) {
    for (const foundation of docGetFoundationSystemFoundations(doc, h)) {
      if (foundation.levelId) {
        oldLevelIds.add(foundation.levelId);
      }
    }
  }
  for (const h of floorSystemHandles) {
    for (const floorHandle of docGetFloorSystemFloorHandles(doc, h)) {
      const floor = doc.floors[floorHandle.id];
      if (floor?.levelId) {
        oldLevelIds.add(floor.levelId);
      }
    }
  }
  for (const handle of refHandles ?? []) {
    if (handle.type === "ANNOTATION") {
      const annotation = doc.annotation[handle.id];
      if (annotation && docAnnotationIsLevelHosted(doc, annotation) && annotation.parentId) {
        levelHostedAnnotationIds.add(handle.id);
        oldLevelIds.add(annotation.parentId);
      }
    }
  }

  if (oldBlockIds.size === 0 && oldLevelIds.size === 0 && oldRoofSystemIds.size === 0) {
    doc = docRemapStairBlockRefsForComponent(doc, {}, memberHandles);
    doc = docRemapFloorRoomRefsForComponent(doc, memberHandles);
    return doc;
  }
  const oldLevelOrder = Object.keys(doc.levels).sort((a, b) => doc.levels[a].sortIndex - doc.levels[b].sortIndex);
  const oldBlockOrder = Object.keys(doc.blocks).sort((a, b) => doc.blocks[a].sortIndex - doc.blocks[b].sortIndex);
  const oldRoofSystemOrder = Object.keys(doc.roofSystems).sort(
    (a, b) => doc.roofSystems[a].sortIndex - doc.roofSystems[b].sortIndex,
  );

  // 1. Duplicate levels
  const nextLevelSortIndex = nextSortIndexFromTable(doc.levels);
  const { updatedEntities: updatedLevels, originalEntities: oldToNewLevelId } = docEntityTableUpdateSome<"LEVEL">(
    doc.levels,
    (level) => oldLevelIds.has(level.id),
    (level) => {
      const oldIndex = oldLevelOrder.indexOf(level.id);
      return { sortIndex: nextLevelSortIndex + oldIndex };
    },
    true,
    true,
  );
  doc = { ...doc, levels: updatedLevels };
  doc = updateLevelPlacement(doc, oldToNewLevelId);
  if (oldToNewLevelIdOut) {
    Object.assign(oldToNewLevelIdOut, oldToNewLevelId);
  }

  // 2. Duplicate blocks, remapping them to the new levels.
  const nextBlockSortIndex = nextSortIndexFromTable(doc.blocks);
  const { updatedEntities: updatedBlocks, originalEntities: oldToNewBlockId } = docEntityTableUpdateSome<"BLOCK">(
    doc.blocks,
    (block) => oldBlockIds.has(block.id),
    (block) => {
      const oldIndex = oldBlockOrder.indexOf(block.id);
      return {
        levelId: oldToNewLevelId[block.levelId],
        sortIndex: nextBlockSortIndex + oldIndex,
      };
    },
    true,
    true,
  );
  doc = { ...doc, blocks: updatedBlocks };

  // 3. Duplicate roof systems.
  const nextRoofSystemSortIndex = nextSortIndexFromTable(doc.roofSystems);
  const { updatedEntities: updatedRoofSystems, originalEntities: oldToNewRoofSystemId } =
    docEntityTableUpdateSome<"ROOF_SYSTEM">(
      doc.roofSystems,
      (roofSystem) => oldRoofSystemIds.has(roofSystem.id),
      (roofSystem) => {
        const oldIndex = oldRoofSystemOrder.indexOf(roofSystem.id);
        return { sortIndex: nextRoofSystemSortIndex + oldIndex };
      },
      true,
      true,
    );
  doc = { ...doc, roofSystems: updatedRoofSystems };

  // 4. Update members to point to the new blocks, levels, and roof systems.
  const oldToNewGroupId: Record<string, string> = {};

  const updateBlockGroup = (doc: Doc, oldBlockId: string, newBlockId: string): Doc => {
    const oldGroup = docGetEntityGroup(doc, { type: "BLOCK", id: oldBlockId });
    if (oldGroup) {
      const oldBlockGroupId = oldGroup.id;
      let newGroupId = oldToNewGroupId[oldBlockGroupId];
      if (!newGroupId) {
        newGroupId = docEntityIdFromString("GROUP", uid());
        doc = mergeIn(doc, ["groups", newGroupId], {
          ...oldGroup,
          id: newGroupId,
          sortIndex: nextSortIndexFromTable(doc.groups),
          isBuiltIn: false,
        });
        oldToNewGroupId[oldBlockGroupId] = newGroupId;
      }
      doc = setIn(doc, ["entityGroups", docEntityHandleHash({ type: "BLOCK", id: newBlockId })], newGroupId);
    }
    return doc;
  };

  // Update rooms to new block IDs.
  for (const roomHandle of roomHandles) {
    const oldBlockId = roomIdToBlockId[roomHandle.id];
    if (!oldBlockId) {
      continue;
    }

    const newBlockId = oldToNewBlockId[oldBlockId];
    if (newBlockId) {
      // Update the room to point to the new block.
      doc = mergeIn(doc, ["rooms", roomHandle.id], { parentId: newBlockId });
      doc = updateBlockGroup(doc, oldBlockId, newBlockId);
    }
  }

  // Update roofs to new block IDs, level IDs, or roof system IDs.
  for (const roofHandle of roofHandles) {
    const roof = doc.roofs[roofHandle.id];
    if (!roof) {
      continue;
    }

    // Remap parent roof system.
    if (roof.parentId && oldToNewRoofSystemId[roof.parentId]) {
      doc = mergeIn(doc, ["roofs", roofHandle.id], { parentId: oldToNewRoofSystemId[roof.parentId] });
    }

    if (isVirtualRoofBlock(roof.blocks)) {
      const oldLevelId = roof.blocks.levelUsage.entityId;
      const newLevelId = oldToNewLevelId[oldLevelId];
      if (newLevelId && newLevelId !== oldLevelId) {
        doc = mergeIn(doc, ["roofs", roofHandle.id, "blocks", "levelUsage"], { entityId: newLevelId });
      }
    } else {
      const oldRoofBlockIds = roofIdToBlockIds[roofHandle.id];
      if (!oldRoofBlockIds) {
        continue;
      }

      const newBlockIds = oldRoofBlockIds.map((id) => oldToNewBlockId[id] ?? id);
      doc = mergeIn(doc, ["roofs", roofHandle.id], { blocks: newBlockIds });

      // Update groups for any blocks that were remapped.
      for (let i = 0; i < oldRoofBlockIds.length; i++) {
        if (newBlockIds[i] !== oldRoofBlockIds[i]) {
          doc = updateBlockGroup(doc, oldRoofBlockIds[i], newBlockIds[i]);
        }
      }
    }
  }

  for (const handle of stairHandles) {
    const stair = doc.stairSystems[handle.id];
    if (!stair?.levelId) {
      continue;
    }
    const newLevelId = oldToNewLevelId[stair.levelId];
    if (!newLevelId) {
      continue;
    }
    doc = mergeIn(doc, ["stairSystems", handle.id], { levelId: newLevelId });
  }

  doc = docRemapStairBlockRefsForComponent(doc, oldToNewLevelId, memberHandles);
  doc = docRemapFloorRoomRefsForComponent(doc, memberHandles);

  for (const handle of railingHandles) {
    const railing = doc.railings[handle.id];
    if (!railing?.levelId) {
      continue;
    }
    const newLevelId = oldToNewLevelId[railing.levelId];
    if (!newLevelId) {
      continue;
    }
    doc = mergeIn(doc, ["railings", handle.id], { levelId: newLevelId });
  }

  for (const handle of foundationSystemHandles) {
    for (const foundation of docGetFoundationSystemFoundations(doc, handle)) {
      if (!foundation.levelId) {
        continue;
      }
      const newLevelId = oldToNewLevelId[foundation.levelId];
      if (!newLevelId) {
        continue;
      }
      doc = mergeIn(doc, ["foundations", foundation.id], { levelId: newLevelId });
    }
  }

  for (const handle of floorSystemHandles) {
    for (const floorHandle of docGetFloorSystemFloorHandles(doc, handle)) {
      const floor = doc.floors[floorHandle.id];
      if (!floor?.levelId) {
        continue;
      }
      const newLevelId = oldToNewLevelId[floor.levelId];
      if (!newLevelId) {
        continue;
      }
      doc = mergeIn(doc, ["floors", floorHandle.id], { levelId: newLevelId });
    }
  }

  for (const annotationId of levelHostedAnnotationIds) {
    const annotation = doc.annotation[annotationId];
    if (!annotation?.parentId) {
      continue;
    }
    const newLevelId = oldToNewLevelId[annotation.parentId];
    if (!newLevelId) {
      continue;
    }
    doc = mergeIn(doc, ["annotation", annotationId], { parentId: newLevelId });
  }

  const newLevelIds = Object.values(oldToNewLevelId);
  doc = docAddDefaultViewsForLevels(doc, newLevelIds, componentId);

  const newLevelHandles = newLevelIds.map((id) => ({ type: "LEVEL", id }) as LevelHandle);
  const newBlockHandles = Object.values(oldToNewBlockId).map((id) => ({ type: "BLOCK", id }) as BlockHandle);
  const newRoofSystemHandles = Object.values(oldToNewRoofSystemId).map(
    (id) => ({ type: "ROOF_SYSTEM", id }) as RoofSystemHandle,
  );

  return docAddComponentMembers(doc, componentId, [...newLevelHandles, ...newBlockHandles, ...newRoofSystemHandles]);
}

/**
 * Ensures a component has at least one level and block.
 * - No level members: create a default level + block.
 * - Level members but no block members (e.g. stair/railing-only copy): add one block on an arbitrary component level.
 * - Already has any block member: leave level/block structure as-is, but ensure default views exist.
 */
export function docEnforceComponentStructure(doc: Doc, componentId: string): { doc: Doc; blockHandle: BlockHandle } {
  const componentHandle: ComponentHandle = { type: "COMPONENT", id: componentId };
  const existingBlocks = docGetComponentMembersByType(doc, componentHandle, "BLOCK");
  const existingLevels = docGetComponentMembersByType(doc, componentHandle, "LEVEL");

  if (existingBlocks.length > 0) {
    const levelIds = docGetComponentMembersByType(doc, componentHandle, "LEVEL").map((handle) => handle.id);
    doc = docEnsureComponentDefaultViews(doc, componentId, levelIds);
    return { doc, blockHandle: existingBlocks[0] };
  }

  let levelId: string;
  if (existingLevels.length === 0) {
    levelId = uid();
    doc = mergeIn(doc, ["levels", levelId], {
      entityType: "LEVEL",
      id: levelId,
      name: "Level 1",
      sortIndex: nextSortIndexFromTable(doc.levels),
      topOfWall: levelInchesNew(108),
      enableAutoGeneratedFoundation: true,
      enableAutoGeneratedFloorSystem: true,
      placement: stackedLevelPlacementNew(),
    });
  } else {
    levelId = existingLevels[0].id;
  }

  const blockId = uid();
  doc = mergeIn(
    doc,
    ["blocks", blockId],
    blockNew({
      id: blockId,
      name: "Block",
      sortIndex: nextSortIndexFromTable(doc.blocks),
      levelId,
    }),
  );

  doc = docAddDefaultViewsForLevels(doc, [levelId], componentId);

  const levelHandle: LevelHandle = { type: "LEVEL", id: levelId };
  const blockHandle: BlockHandle = { type: "BLOCK", id: blockId };
  doc = docAddComponentMembers(doc, componentId, [levelHandle, blockHandle]);

  return { doc, blockHandle };
}

// Returns true if adding a component instance to a component would create a circular dependency.
function hasCircularDependency(doc: Doc, componentInstanceId: string, componentStack: string[] = []): boolean {
  const componentInstanceHandle: { type: "COMPONENT_INSTANCE"; id: string } = {
    type: "COMPONENT_INSTANCE",
    id: componentInstanceId,
  };
  const componentInstance = docGetTableEntity(doc, componentInstanceHandle);
  if (!componentInstance) {
    return false;
  }

  const componentId = componentInstance.componentId;

  // If the component is in the stack, we have a circular dependency.
  if (componentStack.includes(componentId)) {
    return true;
  }

  // Recursively check all component instances in the child component.
  const childComponentHandle: ComponentHandle = { type: "COMPONENT", id: componentId };
  const childInstanceHandles = docGetComponentMembersByType(doc, childComponentHandle, "COMPONENT_INSTANCE");
  const newStack = [...componentStack, componentId];

  for (const childInstanceHandle of childInstanceHandles) {
    const childInstanceId = docGetEntityId(doc, childInstanceHandle);
    if (childInstanceId && hasCircularDependency(doc, childInstanceId, newStack)) {
      return true;
    }
  }
  return false;
}

/**
 * Returns true if changing a component instance to point to a different component would
 * create a circular dependency.
 */
export function componentChangeCreatesCircularDependency(
  doc: Doc,
  handle: ComponentInstanceHandle,
  newComponentId: string,
): boolean {
  const parentComponent = docEntityGetComponent(doc, handle);
  if (!parentComponent) {
    return false;
  }

  const simulatedDoc = mergeIn(doc, ["componentInstances", handle.id], { componentId: newComponentId });
  return hasCircularDependency(simulatedDoc, handle.id, [parentComponent.id]);
}

export function docAddComponentMembers(
  doc: Doc,
  componentId: string,
  memberHandles: ComponentMemberEntityHandle | ComponentMemberEntityHandle[],
): Doc {
  let handles = Array.isArray(memberHandles) ? memberHandles : [memberHandles];
  handles = expandRoofRelatedMemberHandles(doc, handles);
  ({ doc, handles } = docReparentComponentRoofs(doc, handles));

  // First, check if any eligible entity has a reportable unit label
  for (const entityHandle of handles) {
    // Check if already in this component - skip these
    if (isComponentAssociatedEntityHandle(entityHandle)) {
      if (docGetComponentIdsForAssociatedEntity(doc, entityHandle).includes(componentId)) {
        continue;
      }
    } else {
      const existingComponent = docEntityGetComponent(doc, entityHandle);
      if (existingComponent && existingComponent.id === componentId) {
        continue;
      }
    }
    // Check if entity has a reportable unit label - cannot add to component
    if (docEntityHandleIsLabelable(entityHandle) && docEntityHasAnyReportableUnitDesignatorLabel(doc, entityHandle)) {
      throw new DocEntityTransactErrorNotification(
        "Cannot add an entity with a reportable unit label to a component.",
        entityHandle.type,
      );
    }
  }

  // Check for circular dependencies when adding component instances.
  for (const entityHandle of handles) {
    if (entityHandle.type === "COMPONENT_INSTANCE") {
      const targetEntityId = docGetEntityId(doc, entityHandle);
      if (targetEntityId && hasCircularDependency(doc, targetEntityId, [componentId])) {
        throw new ComponentCircularDependencyErrorNotification(componentId);
      }
    }
  }

  // Add entities to componentMembers hash table.
  for (const entityHandle of handles) {
    if (isComponentAssociatedEntityHandle(entityHandle)) {
      doc = docAddAssociatedEntityToComponent(doc, entityHandle, componentId);
    } else {
      const hash = docEntityHandleHash(entityHandle);
      doc = setIn(doc, ["componentMembers", hash], componentId);
    }
  }

  return doc;
}

/**
 * Registers a newly created entity with the component the editor is isolated into, if any. Entities
 * added while a component is isolated belong to that component; entities added in the main world
 * belong to none. Callers resolve the isolated component id editor-side and pass it in, so this
 * stays editor-free.
 */
export function docAddComponentMembershipIfIsolated(
  doc: Doc,
  handle: DocEntityHandle,
  isolatedComponentId: string | undefined,
): Doc {
  if (!isolatedComponentId || !isComponentMemberEntityHandle(handle)) {
    return doc;
  }

  return docAddComponentMembers(doc, isolatedComponentId, handle);
}

function docAddAssociatedEntityToComponent(
  doc: Doc,
  entityHandle: ComponentAssociatedEntityHandle,
  componentId: string,
): Doc {
  switch (entityHandle.type) {
    case "OPTION_SET":
      return docAddOptionSetToComponent(doc, entityHandle, componentId);
    default:
      assertUnreachable(entityHandle.type);
  }
}

export function docAddOptionSetToComponent(doc: Doc, handle: OptionSetHandle, componentId: string): Doc {
  return docSyncRefMembership(doc, doc, handle, componentId, "add", {
    containerIncluded: true,
    memberIncludedByContainer: true,
    srcPolicy: "ensure",
  });
}

export function docRemoveOptionSetFromComponent(doc: Doc, optionSetHandle: OptionSetHandle, componentId: string): Doc {
  return docSyncRefMembership(doc, undefined, optionSetHandle, componentId, "remove");
}

/**
 * Recursively gets all option sets that belong to a component or any of its nested component instances.
 */
export function docGetComponentRecursiveOptionSets(
  doc: Doc,
  componentHandle: ComponentHandle,
  visitedComponents: Set<string> = new Set(),
): OptionSetHandle[] {
  if (visitedComponents.has(componentHandle.id)) {
    return [];
  }
  visitedComponents.add(componentHandle.id);

  const optionSets: OptionSetHandle[] = docGetComponentMembersByType(doc, componentHandle, "OPTION_SET");

  const componentInstances = docGetComponentMembersByType(doc, componentHandle, "COMPONENT_INSTANCE");
  for (const ciHandle of componentInstances) {
    const ci = doc.componentInstances[ciHandle.id];
    if (ci) {
      optionSets.push(
        ...docGetComponentRecursiveOptionSets(doc, { type: "COMPONENT", id: ci.componentId }, visitedComponents),
      );
    }
  }

  // Deduplicate option sets by ID
  const uniqueOptionSets: OptionSetHandle[] = [];
  const seenIds = new Set<string>();
  for (const handle of optionSets) {
    if (!seenIds.has(handle.id)) {
      seenIds.add(handle.id);
      uniqueOptionSets.push(handle);
    }
  }

  return uniqueOptionSets;
}
